import { useEffect, useState } from "react";
import arrowLeft from "../../Assets/icon/arrow-narrow-left.svg";
import { useNavigate } from "react-router-dom";
import { useHeader } from "../../Providers/HeaderContextProvider";
import {
  getUserSubscriptionDetails,
  getProducts,
  billingPortal,
  cancelMembership,
  reactivateSubscription,
  transferuser,
} from "../../Services/subscription";
import { deleteAccount } from "../../Services/MyProfile";
import { useSearchParams } from "react-router-dom";
import LoadingScreen from "../LoadingPage/ContentLoader";
import ChangePlanModal from "./ChangePlanModal";
import GenericModal from "../../Components/Modal/GenericModal";
import AddMembersModal from "../../Components/MembershipAddmemberModal";
import AssignMembershipModal from "../../Components/AssignMembershipModal";
import ContactCard from "./ContactCard";
import "./ManageMembershipNew.css";

function ManageMembershipNew() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setHeaderContent } = useHeader();
  
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  const [showSubscriptionView, setShowSubscriptionView] = useState(false);
  const [showSubscriptionDetails, setShowSubscriptionDetails] = useState(false);
  const [showChangePlanModal, setShowChangePlanModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  const [showReactivateModal, setShowReactivateModal] = useState(false);
  const [showAdditionalMemberships, setShowAdditionalMemberships] = useState(false);
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [showAssignMemberModal, setShowAssignMemberModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [expandedMemberId, setExpandedMemberId] = useState(null);
  const [showTransferOwnershipModal, setShowTransferOwnershipModal] = useState(false);
  const [memberToTransfer, setMemberToTransfer] = useState(null);
  const [selectedLicence, setSelectedLicence] = useState(null);
  const [unAssignedLicenceData, setUnAssignedLicenceData] = useState([]);
  const [assignedLicenceData, setAssignedLicenceData] = useState([]);

  useEffect(() => {
    setHeaderContent(false);
    fetchSubscriptionData();
    fetchProducts();
  }, []);

  const fetchSubscriptionData = async () => {
    try {
      setLoading(true);
      const response = await getUserSubscriptionDetails();
      const data = response?.payload || response?.data;
      
      // Handle new v2 API response structure
      if (Array.isArray(data?.subscriptions)) {
        const sub = data.subscriptions[0];
        if (sub) {
          // Extract license assignments
          const licenseAssignments = Array.isArray(sub?.license_assignments) ? sub.license_assignments : [];
          
          // Pending licenses (unassigned)
          const pendingLicences = licenseAssignments.filter((a) => a?.status === "pending");
          const mappedPending = pendingLicences.map((a) => ({
            license_id: a?.assignment_id,
            user_id: a?.assigned_user_id,
            first_name: "Assign",
            last_name: "Member",
            profile_pic: "",
            email: a?.invited_email || "No Email Provided",
            status: "pending",
          }));
          setUnAssignedLicenceData(mappedPending);
          
          // Active licenses (assigned)
          const activeLicences = licenseAssignments.filter((a) => a?.status === "active");
          const mappedActive = activeLicences.map((a) => ({
            license_id: a?.assignment_id,
            user_id: a?.assigned_user_id,
            first_name: a?.first_name || "Assigned",
            last_name: a?.last_name || "User",
            profile_pic: a?.profile_pic || "",
            email: a?.email || a?.invited_email || "",
            status: "active",
          }));
          setAssignedLicenceData(mappedActive);
        }
      }
      
      setSubscriptionData(data);
    } catch (err) {
      setError("Failed to load subscription data");
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await getProducts();
      setProducts(response?.data || []);
    } catch (err) {
      console.error("Failed to load products:", err);
    }
  };

  const handleChangePlan = async () => {
    try {
      const origin = window.location.origin;
      const subscriptionId = subscription?.subscription_id || subscription?.id;
      if (!subscriptionId) {
        throw new Error("Missing subscription id to open billing portal.");
      }

      // Build allowed price ids for switching plan: include primary main plan price ids (monthly/yearly)
      const mainProduct = Array.isArray(products)
        ? products.find((p) => p?.metadata?.plan_type === "main")
        : null;
      const primaryPrices = (mainProduct?.pricingDetails || []).filter((pd) => {
        return pd?.metadata?.price_type === "primary-users";
      });
      const allowedPriceIds = primaryPrices.map((pd) => pd?.id).filter(Boolean);

      const payload = {
        return_url: `${origin}/manageMembership`,
        type: "subscription",
        payload: {
          subscription_id: subscriptionId,
          allowed_price_ids: allowedPriceIds,
          disable_cancellation: true,
        },
      };

      const res = await billingPortal(payload);
      const url = res?.url || res?.payload?.url;
      if (url) {
        window.location.href = url;
        return;
      }
      throw new Error("No billing portal URL returned");
    } catch (error) {
      setError(
        error?.response?.data?.message || error?.message || "Failed to open billing portal. Please try again later."
      );
    }
  };

  const handleCancelSubscription = async () => {
    try {
      setLoading(true);
      const { cancelMembership } = await import("../../Services/subscription");
      const response = await cancelMembership();
      // Handle new response format
      if (response?.status === 'success' || response?.success) {
        setSuccess("Subscription cancelled successfully");
        setShowCancelModal(false);
        await fetchSubscriptionData();
        // Force reload to ensure UI reflects the new cancelled-at-period-end state
        window.location.reload();
      } else {
        setError("Unable to cancel membership. Please try again later.");
      }
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to cancel subscription");
    } finally {
      setLoading(false);
    }
  };

  const handleReactivateSubscription = async () => {
    try {
      setLoading(true);
      const res = await reactivateSubscription();
      // Handle new response format
      if (res?.status === 'success' || res?.success) {
        setSuccess("Subscription reactivated successfully");
        setShowReactivateModal(false);
        await fetchSubscriptionData();
        window.location.reload();
      } else {
        setError(res?.message || "Unable to reactivate subscription.");
      }
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to reactivate subscription");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    try {
      setLoading(true);
      await deleteAccount();
      setSuccess("Account deleted successfully");
      setShowDeleteAccountModal(false);
      navigate("/");
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to delete account");
    } finally {
      setLoading(false);
    }
  };

  const handleMemberClick = (member) => {
    // Toggle accordion expansion
    if (expandedMemberId === member.license_id) {
      setExpandedMemberId(null);
    } else {
      setExpandedMemberId(member.license_id);
    }
  };

  const handleTransferOwnership = (member) => {
    setMemberToTransfer(member);
    setShowTransferOwnershipModal(true);
  };

  const handleConfirmTransferOwnership = async () => {
    try {
      const payload = {
        user_id: memberToTransfer?.user_id,
        action: 0
      };
      const response = await transferuser(payload);
      if (response?.status === "success" || response?.status === "removed") {
        setSuccess("Ownership transferred successfully");
        setShowTransferOwnershipModal(false);
        setMemberToTransfer(null);
        setExpandedMemberId(null);
        await fetchSubscriptionData();
      } else {
        throw new Error("Transfer failed");
      }
    } catch (error) {
      setError(error?.response?.data?.message || error?.message || "Failed to transfer ownership");
      setShowTransferOwnershipModal(false);
      setMemberToTransfer(null);
    }
  };

  const handleCancelInvitation = async (member) => {
    try {
      // Implement cancel invitation logic
      setSuccess("Invitation cancelled successfully");
      setExpandedMemberId(null);
      await fetchSubscriptionData();
    } catch (error) {
      setError("Failed to cancel invitation");
    }
  };

  const handleCancelMemberSubscription = async (member) => {
    try {
      // Implement cancel member subscription logic
      setSuccess("Member subscription cancelled successfully");
      setExpandedMemberId(null);
      await fetchSubscriptionData();
    } catch (error) {
      setError("Failed to cancel member subscription");
    }
  };

  const handleAssignLicense = (licence) => {
    setSelectedLicence([licence?.license_id]);
    setShowAssignMemberModal(true);
  };

  const handleAddMembershipsClick = async () => {
    try {
      const origin = window.location.origin;
      const subscriptionId = subscription?.subscription_id || subscription?.id;
      const currentPriceId = subscription?.metadata?.current_price?.price_id
        || subscription?.plan?.provider_plan_id?.stripe;
      
      if (!subscriptionId || !currentPriceId) {
        throw new Error("Missing subscription or price information");
      }

      const payload = {
        return_url: `${origin}/manageMembership`,
        type: "add_member",
        payload: {
          subscription_id: subscriptionId,
          allowed_price_ids: [currentPriceId],
          disable_cancellation: true,
        },
      };

      const res = await billingPortal(payload);
      const url = res?.url || res?.payload?.url;
      if (url) {
        window.location.href = url;
      }
    } catch (error) {
      setError(error?.message || "Failed to open billing portal");
    }
  };

  const handleBillingPortal = async () => {
    try {
      const origin = window.location.origin;
      const payload = {
        return_url: `${origin}/manageMembership`,
        type: "payment_method",
        payload: {},
      };

      const res = await billingPortal(payload);
      // Handle new response format
      if (res?.status === 'success' && res?.payload?.url) {
        window.location.href = res.payload.url;
      } else if (res?.url) {
        // Fallback for old format
        window.location.href = res.url;
      } else {
        throw new Error("No billing portal URL returned");
      }
    } catch (error) {
      setError(
        error?.response?.data?.message || "Failed to open billing portal. Please try again later."
      );
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  // Parse new API response structure
  const subscription = subscriptionData?.subscriptions?.[0] || subscriptionData;
  const planName = subscription?.plan?.name || subscription?.subscription_data?.plan_details?.name || "Aris";
  const rawBillingCycle = subscription?.billing_cycle || subscriptionData?.membership_type || "monthly";
  const isYearly = rawBillingCycle === "yearly" || rawBillingCycle === "year";
  const currentBillingCycle = isYearly ? "yearly" : "monthly";
  const isActive = subscription?.status === "active" || subscriptionData?.subscription_data?.status === "active";
  const isCancelled = subscription?.cancel_at_period_end === true || subscriptionData?.subscription_data?.cancel_at_period_end === true;
  const renewalDate = formatDate(subscription?.current_period_end || subscriptionData?.renewal_data);
  const planFeatures = subscription?.plan?.metadata?.plan_features || subscriptionData?.subscription_data?.plan_details?.marketing_features || [];
  
  // Derive price from subscription API (unit_amount is in cents)
  const unitAmountCents = subscription?.metadata?.current_price?.unit_amount
    || subscription?.next_billing_amount
    || subscription?.plan?.amount;
  const planPrice = unitAmountCents ? (unitAmountCents / 100).toFixed(2) : "";
  const billingLabel = isYearly ? "yr" : "mo";
  
  // Get user_type from localStorage profile
  const getUserType = () => {
    try {
      const profileData = localStorage.getItem('profile');
      if (profileData) {
        const profile = JSON.parse(profileData);
        return profile?.user_type || null;
      }
    } catch (error) {
      console.error('Error parsing profile from localStorage:', error);
    }
    return null;
  };
  const userType = getUserType();
  const isPrimaryUser = userType === "primary";

  if (loading) {
    return (
      <div className="manage-membership-page">
        <div className="manage-membership-new">
          <div className="membership-content" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
            <LoadingScreen />
          </div>
        </div>
      </div>
    );
  }

  // Render home view or subscription detail view
  if (showSubscriptionView) {
    return (
      <div className="manage-membership-page">
        {/* Header Section - Outside white container */}
        <header className="page-header-external">
          <button className="back-btn" onClick={() => setShowSubscriptionView(false)}>
            <img src={arrowLeft} alt="Back" />
          </button>
          <h1 className="page-title">Subscription</h1>
        </header>

        {/* White Content Container */}
        <div className="manage-membership-new subscription-detail-view">
        <div className="membership-content">
          {/* Alert Messages */}
          {error && <div className="alert alert-error">{error}</div>}
          {success && <div className="alert alert-success">{success}</div>}

          {/* Current Plan Card */}
          <div className="current-plan-card">
            <div className="plan-label">Current Plan</div>
            <div className="plan-details">
              <div className="plan-name-section">
                <p className="plan-billing">
                  {planPrice ? `$${planPrice}/${billingLabel}` : ""}{planPrice ? `, Billed ${isYearly ? "annually" : "monthly"}` : ""}
                </p>
                <h2 className="plan-name">{planName}</h2>
                <p className="plan-renewal">
                  Renews on {renewalDate}
                </p>
              </div>
              <div className="plan-actions">
                <span className={`plan-status-badge ${isActive ? 'active' : 'cancelled'}`}>
                  {isActive ? 'Active' : 'Cancelled'}
                </span>
                <button 
                  className="details-btn"
                  onClick={() => setShowSubscriptionDetails(true)}
                >
                  Subscription Details
                </button>
              </div>
            </div>
          </div>

          {/* Manage Subscription Section */}
          <div className="manage-subscription-section">
            <div className="section-label">Manage your subscription</div>
            <div className="subscription-actions">
              <button className="action-item" onClick={handleBillingPortal}>
                <span>Manage Payment Method</span>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              <div className="action-separator"></div>
              <button className="action-item" onClick={handleChangePlan}>
                <span>Change Subscription Plan</span>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              <div className="action-separator"></div>
              {isCancelled ? (
                <button className="action-item" onClick={() => setShowReactivateModal(true)}>
                  <span>Reactivate Subscription</span>
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              ) : (
                <button className="action-item" onClick={() => setShowCancelModal(true)}>
                  <span>Cancel Subscription</span>
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              )}
            </div>
          </div>

          {/* Delete Account */}
          <button className="delete-account-btn" onClick={() => setShowDeleteAccountModal(true)}>
            Delete Account
          </button>
        </div>
        </div>

        {/* Modals */}
        <GenericModal
          isOpen={showSubscriptionDetails}
          onClose={() => setShowSubscriptionDetails(false)}
          heading="Subscription Details"
          primaryLabel=""
          secondaryLabel=""
          maxWidth="548px"
        >
          <div className="subscription-details-content">
            <div className="subscription-card-bordered">
              <div className="subscription-header">
                <span className="subscription-label">Current Plan</span>
                <span className={`subscription-badge ${isActive ? 'active' : 'cancelled'}`}>
                  {isActive ? 'Active' : 'Cancelled'}
                </span>
              </div>
              <h3 className="subscription-plan-name">{planName}</h3>
              <p className="subscription-billing-info">
                {planPrice ? `$${planPrice}/${billingLabel}, Billed ${isYearly ? "annually" : "monthly"}` : ""}
              </p>
              <p className="subscription-renewal-info">
                Renews on {renewalDate}
              </p>
            </div>

            <div className="benefits-section">
              <h4 className="benefits-title">Benefits</h4>
              <ul className="benefits-list">
                {planFeatures.map((feature, index) => (
                  <li key={index} className="benefit-item">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <mask id={`path-1-inside-1_${index}`} fill="white">
                        <path d="M1.75 11.75C1.75 6.22715 6.22715 1.75 11.75 1.75C17.2728 1.75 21.75 6.22715 21.75 11.75C21.75 17.2728 17.2728 21.75 11.75 21.75C6.22715 21.75 1.75 17.2728 1.75 11.75Z"/>
                      </mask>
                      <path d="M1.75 11.75C1.75 6.22715 6.22715 1.75 11.75 1.75C17.2728 1.75 21.75 6.22715 21.75 11.75C21.75 17.2728 17.2728 21.75 11.75 21.75C6.22715 21.75 1.75 17.2728 1.75 11.75Z" fill="#F7F7F7"/>
                      <path d="M11.75 21.75V21.125C6.57233 21.125 2.375 16.9277 2.375 11.75H1.75H1.125C1.125 17.618 5.88197 22.375 11.75 22.375V21.75ZM21.75 11.75H21.125C21.125 16.9277 16.9277 21.125 11.75 21.125V21.75V22.375C17.618 22.375 22.375 17.618 22.375 11.75H21.75ZM11.75 1.75V2.375C16.9277 2.375 21.125 6.57233 21.125 11.75H21.75H22.375C22.375 5.88197 17.618 1.125 11.75 1.125V1.75ZM11.75 1.75V1.125C5.88197 1.125 1.125 5.88197 1.125 11.75H1.75H2.375C2.375 6.57233 6.57233 2.375 11.75 2.375V1.75Z" fill="url(#paint0_linear_6329_5415)" mask={`url(#path-1-inside-1_${index})`}/>
                      <path fillRule="evenodd" clipRule="evenodd" d="M15.3461 8.51695C15.6925 8.1617 16.2853 8.1329 16.6701 8.45263C17.055 8.77235 17.0862 9.31952 16.7398 9.67477L11.1148 15.444C10.7555 15.8125 10.1349 15.8276 9.75505 15.477L7.25506 13.1693C6.88894 12.8314 6.88894 12.2834 7.25506 11.9455C7.62117 11.6075 8.21476 11.6075 8.58088 11.9455L10.3822 13.6082L15.3461 8.51695Z" fill="#149041"/>
                      <defs>
                        <linearGradient id="paint0_linear_6329_5415" x1="11.75" y1="1.75" x2="11.75" y2="21.75" gradientUnits="userSpaceOnUse">
                          <stop stopColor="#F7F7F7"/>
                          <stop offset="1" stopColor="#BBD0E8"/>
                        </linearGradient>
                      </defs>
                    </svg>
                    <span>{typeof feature === 'string' ? feature.replace(/<\/?b>/g, '') : feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </GenericModal>

        <ChangePlanModal
          isOpen={showChangePlanModal}
          onClose={() => setShowChangePlanModal(false)}
          currentPlan={subscriptionData?.subscription_data?.plan_details}
          availablePlans={products}
          onChangePlan={handleChangePlan}
          subscriptionData={subscriptionData}
        />

        <GenericModal
          isOpen={showCancelModal}
          onClose={() => setShowCancelModal(false)}
          heading="Cancel Subscription"
          primaryLabel={loading ? 'Cancelling...' : 'Yes, Cancel'}
          secondaryLabel="Back"
          onPrimary={handleCancelSubscription}
          onSecondary={() => setShowCancelModal(false)}
          primaryVariant="danger"
          loading={loading}
          maxWidth="400px"
        >
          <p style={{ margin: 0, textAlign: 'center' }}>Are you sure you want to cancel your subscription?</p>
        </GenericModal>

        <GenericModal
          isOpen={showReactivateModal}
          onClose={() => setShowReactivateModal(false)}
          heading="Reactivate Subscription"
          primaryLabel={loading ? 'Reactivating...' : 'Yes, Reactivate'}
          secondaryLabel="Back"
          onPrimary={handleReactivateSubscription}
          onSecondary={() => setShowReactivateModal(false)}
          primaryVariant="primary"
          loading={loading}
          maxWidth="400px"
        >
          <p style={{ margin: 0, textAlign: 'center' }}>Would you like to reactivate your subscription?</p>
        </GenericModal>

        <GenericModal
          isOpen={showDeleteAccountModal}
          onClose={() => setShowDeleteAccountModal(false)}
          heading="Delete Account"
          primaryLabel={loading ? 'Deleting...' : 'Yes, Delete'}
          secondaryLabel="Back"
          onPrimary={handleDeleteAccount}
          onSecondary={() => setShowDeleteAccountModal(false)}
          primaryVariant="danger"
          loading={loading}
          maxWidth="400px"
        >
          <p style={{ margin: 0, textAlign: 'center' }}>Are you sure you want to delete your account? This action cannot be undone.</p>
        </GenericModal>
      </div>
    );
  }

  // Additional Memberships View
  if (showAdditionalMemberships) {
    return (
      <div className="manage-membership-page">
        {/* Header Section - Outside white container */}
        <header className="page-header-external">
          <button className="back-btn" onClick={() => setShowAdditionalMemberships(false)}>
            <img src={arrowLeft} alt="Back" />
          </button>
          <h1 className="page-title">Additional Memberships</h1>
        </header>

        {/* White Content Container */}
        <div className="manage-membership-new">
          <div className="membership-content">
            {/* Alert Messages */}
            {error && <div className="alert alert-error">{error}</div>}
            {success && <div className="alert alert-success">{success}</div>}

            {/* Plan Info Card */}
            <div className="additional-members-plan-card">
              <div className="plan-info-section">
                <div className="plan-label-small">Plan</div>
                <h3 className="plan-name-large">{planName}</h3>
                <p className="plan-billing-small">
                  {planPrice ? `$${planPrice}/${billingLabel}, Billed ${isYearly ? "annually" : "monthly"}` : ""}
                </p>
              </div>
              <button className="add-membership-btn" onClick={() => setShowAddMemberModal(true)}>
                Add Membership
              </button>
            </div>

            {/* Members Section */}
            {(assignedLicenceData.length > 0 || unAssignedLicenceData.length > 0) && (
              <div className="members-section">
                <div className="members-label">Members</div>
                <div className="members-list">
                  {/* Unassigned Licenses */}
                  {unAssignedLicenceData.map((licence) => (
                    <div key={licence.license_id} className="member-card" onClick={() => handleAssignLicense(licence)}>
                      <div className="member-avatar">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                          <circle cx="12" cy="12" r="10" fill="#E5E7EB"/>
                          <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" fill="#9CA3AF"/>
                        </svg>
                      </div>
                      <div className="member-info">
                        <div className="member-name">Assign Member</div>
                        <div className="member-email">{licence.email}</div>
                      </div>
                      <svg className="member-arrow" width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                  ))}

                  {/* Assigned Members */}
                  {assignedLicenceData.map((member) => {
                    const isExpanded = expandedMemberId === member.license_id;
                    const isInvited = member.status === 'invited' || !member.user_id;
                    return (
                      <div key={member.license_id} className="member-card-wrapper">
                        <div className="member-card" onClick={() => handleMemberClick(member)}>
                          <div className="member-avatar">
                            {member.profile_pic ? (
                              <img src={member.profile_pic} alt={`${member.first_name} ${member.last_name}`} />
                            ) : (
                              <div className="member-initials">
                                {member.first_name?.[0]?.toUpperCase()}{member.last_name?.[0]?.toUpperCase()}
                              </div>
                            )}
                          </div>
                          <div className="member-info">
                            <div className="member-name">{member.first_name} {member.last_name}</div>
                            <div className="member-email">{member.email}</div>
                          </div>
                          <span className={`member-status-badge ${isInvited ? 'invited' : 'active'}`}>
                            {isInvited ? 'Invited' : 'Active'}
                          </span>
                          <svg className={`member-chevron ${isExpanded ? 'expanded' : ''}`} width="20" height="20" viewBox="0 0 20 20" fill="none">
                            <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </div>
                        
                        {isExpanded && (
                          <div className="member-accordion-content">
                            {/* Plan Information Section */}
                            <div className="accordion-section">
                              <div className="accordion-section-label">Plan</div>
                              <div className="accordion-plan-name">{planName}</div>
                              <div className="accordion-plan-billing">
                                {planPrice ? `$${planPrice}/${billingLabel}, Billed ${isYearly ? "annually" : "monthly"}` : ""}
                              </div>
                              <div className="accordion-plan-renewal">Renews on {renewalDate}</div>
                            </div>

                            {/* Manage Membership Section */}
                            <div className="accordion-section">
                              <div className="accordion-section-label">Manage membership</div>
                              
                              {isInvited ? (
                                <>
                                  <button className="accordion-action-item" onClick={() => handleTransferOwnership(member)}>
                                    <span>Transfer Ownership</span>
                                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                      <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button className="accordion-action-item" onClick={() => handleCancelInvitation(member)}>
                                    <span>Cancel Invitation</span>
                                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                      <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                  </button>
                                  <button className="accordion-action-item" onClick={() => handleCancelMemberSubscription(member)}>
                                    <span>Cancel Subscription</span>
                                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                      <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Add Member Modal */}
        <AddMembersModal
          isAddOpen={showAddMemberModal}
          setisAddOpen={setShowAddMemberModal}
          setisInviteOpen={() => {}}
          memberContIncrement={() => {}}
          secondary_price={unitAmountCents ? (unitAmountCents / 100) : null}
          products={products}
          subscriptionData={subscription}
          planName={planName}
          billingCycle={currentBillingCycle}
          setError={setError}
        />

        {/* Assign Member Modal */}
        {showAssignMemberModal && (
          <AssignMembershipModal
            open={showAssignMemberModal}
            selectedLicence={selectedLicence}
            onClose={async ({ success }) => {
              setShowAssignMemberModal(false);
              if (success) {
                setSuccess("Membership assigned successfully");
                await fetchSubscriptionData();
              }
            }}
          />
        )}

        {/* Transfer Ownership Modal */}
        {showTransferOwnershipModal && memberToTransfer && (
          <GenericModal
            isOpen={showTransferOwnershipModal}
            onClose={() => {
              setShowTransferOwnershipModal(false);
              setMemberToTransfer(null);
            }}
            heading="Transfer Ownership"
            primaryLabel="Yes, Transfer Ownership"
            secondaryLabel="Cancel"
            onPrimary={handleConfirmTransferOwnership}
            onSecondary={() => {
              setShowTransferOwnershipModal(false);
              setMemberToTransfer(null);
            }}
            maxWidth="500px"
          >
            <div className="transfer-ownership-content">
              <p className="transfer-ownership-text">
                Are you sure you want to transfer ownership to <strong>{memberToTransfer.first_name} {memberToTransfer.last_name}</strong>?
              </p>
              <p className="transfer-ownership-text">
                By transferring ownership, they will become the primary account holder with full responsibility for managing the subscription and handling all future payments.
              </p>
            </div>
          </GenericModal>
        )}
      </div>
    );
  }
  
  // Home View
  return (
    <div className="manage-membership-page">
      {/* Header Section - Outside white container */}
      <header className="page-header-external">
        <button className="back-btn" onClick={() => navigate("/dashboard")}>
          <img src={arrowLeft} alt="Back" />
        </button>
        <h1 className="page-title">Manage Membership</h1>
      </header>

      {/* White Content Container */}
      <div className="manage-membership-new">
      <div className="membership-content">
        {/* Alert Messages */}
        {error && <div className="alert alert-error">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}

        {/* Subscription Menu Item */}
        <div className="menu-section">
          <button className="simple-menu-item" onClick={() => setShowSubscriptionView(true)}>
            <div className="menu-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <rect x="3" y="6" width="18" height="15" rx="2" stroke="currentColor" strokeWidth="2"/>
                <path d="M3 10h18" stroke="currentColor" strokeWidth="2"/>
                <path d="M7 3v4M17 3v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </div>
            <div className="menu-text">
              <div className="menu-title">Subscription</div>
              <div className="menu-subtitle">{planName} Plan, renews on {renewalDate}</div>
            </div>
            <svg className="menu-arrow" width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>

        {/* Additional Memberships Section - Only for primary users */}
        {isPrimaryUser && (
          <div className="menu-section">
            <button className="simple-menu-item" onClick={() => setShowAdditionalMemberships(true)}>
              <div className="menu-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="2"/>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className="menu-text">
                <div className="menu-title">Additional Memberships</div>
                <div className="menu-subtitle">Invite family, friends, or teammates to grow with Aris</div>
              </div>
              <svg className="menu-arrow" width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
        )}
      </div>
      </div>
    </div>
  );
  
}

export default ManageMembershipNew;
